import { useEffect, useState, type ReactNode } from 'react'
import { Pressable } from '@ayble-health/dashboard-components'
import { Button } from 'components/ui/button'
import { Chip } from 'components/ui/chip'
import { AybleMarkIcon, CloseIcon, FileIcon } from './icons'
import {
  Chevron,
  RemovalPanel,
  Thumbs,
  valenceChipVariant,
  type AiReview,
} from './CareBlockDrawer'
import { AI_INSIGHT_REMOVAL_REASONS } from '../../models/careBlockDetail'
import type { PatientSummaryDetail, SummaryMeeting } from '../../models/careOSPrototype'

/**
 * Patient Summary drawer — the detail behind the steady-state Patient Summary
 * card, opened by clicking it the way a Care Guide card opens its block.
 *
 * Shares the Care Block Detail drawer's shell (half-viewport slide-in, Recoleta
 * title, round close) and its AI review: every section is Ayble AI output, so
 * each carries the block card's thumbs — up accepts it, down opens the same
 * "Why remove this?" reasons. Unlike a feed entry, a removed section leaves an
 * Undo row behind rather than vanishing, since it's a whole part of the
 * summary. Reviews are local and unsaved, like the block drawer's.
 */

interface Props {
  isOpen: boolean
  detail: PatientSummaryDetail
  onClose: () => void
}

type SectionId = 'highlights' | 'progress' | 'sentiment' | 'journey'

const bodyText = {
  margin: 0, fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)',
  color: 'var(--evergreen)',
} as const

const sectionLabel = {
  margin: 0, fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)',
  letterSpacing: '0.01em', color: 'var(--seafoam)',
} as const

const bulletList = {
  ...bodyText, paddingLeft: 18,
  display: 'flex', flexDirection: 'column', gap: 6,
} as const

/** The block drawer's card: white, cloud hairline, Seafoam rule on the left. */
function SectionCard({ background = 'var(--canvas)', children }: { background?: string; children: ReactNode }) {
  return (
    <div style={{
      overflow: 'hidden', background,
      border: '1px solid var(--cloud)', borderLeft: '3px solid var(--seafoam)',
      borderRadius: 'var(--radius-card, 8px)',
    }}>
      {children}
    </div>
  )
}

/** One reviewable section: header with thumbs, its content, and the removal flow. */
function Section({ title, review, onReview, children }: {
  title: string
  review?: AiReview
  onReview: (next: AiReview | undefined) => void
  children: ReactNode
}) {
  if (review?.status === 'removed') {
    const reason = AI_INSIGHT_REMOVAL_REASONS.find(r => r.value === review.reason)?.label
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        border: '1.5px dashed var(--cloud)', borderRadius: 'var(--radius-card, 8px)',
        ...bodyText, color: 'var(--blue-grey)',
      }}>
        <span style={{ flex: 1, minWidth: 0 }}>{title} removed{reason ? ` · ${reason}` : ''}</span>
        <Button variant="outline" size="xs" onClick={() => onReview(undefined)}>Undo</Button>
      </div>
    )
  }

  const accepted = review?.status === 'accepted'
  const removing = review?.status === 'removing'

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 24 }}>
        <h3 style={sectionLabel}>{title}</h3>
        {accepted && (
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)' }}>
            · Accepted
          </span>
        )}
        {!accepted && !removing && (
          <span style={{ marginLeft: 'auto' }}>
            <Thumbs onChange={direction => onReview({ status: direction === 'up' ? 'accepted' : 'removing' })} />
          </span>
        )}
      </div>
      {children}
      {review?.status === 'removing' && (
        <SectionCard>
          <RemovalPanel review={review} onChange={onReview} />
        </SectionCard>
      )}
    </section>
  )
}

function MeetingRow({ meeting, open, onToggle }: { meeting: SummaryMeeting; open: boolean; onToggle: () => void }) {
  const items = meeting.plan.map(item => (
    <li key={item.label}>
      <strong style={{ fontWeight: 'var(--text-sm-b-weight)' }}>{item.label}:</strong> {item.text}
    </li>
  ))
  return (
    <SectionCard>
      <Pressable
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        display="flex"
        // flexDirection="row" as a Chakra prop: Pressable defaults to a column,
        // which stacks the date, visit and chevron and triples the row height.
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        gap="8px"
        w="100%"
        textAlign="left"
        p="10px 14px"
      >
        <span style={{ ...bodyText, fontWeight: 'var(--text-sm-b-weight)' }}>{meeting.date}</span>
        <span style={{ ...bodyText, fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)' }}>
          {meeting.visitType} · {meeting.provider}
        </span>
        <span style={{ marginLeft: 'auto' }}><Chevron open={open} /></span>
      </Pressable>
      {open && (
        <div style={{
          padding: '10px 14px 12px', borderTop: '1px solid var(--cloud)',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <p style={sectionLabel}>Engagement plan</p>
          {meeting.planStyle === 'goals'
            ? <ol style={{ ...bulletList, paddingLeft: 20 }}>{items}</ol>
            : <ul style={bulletList}>{items}</ul>}
          <span>
            {/* Compact: the block drawer's full-size ChartNoteButton reads as a
                primary action here. Affordance only — nowhere to navigate yet. */}
            <Button variant="outline" size="xs" gap="6px">
              <FileIcon />
              Chart note
              <span style={{ color: 'var(--blue-grey)', fontWeight: 'var(--text-xs-weight)' }}>{meeting.date}</span>
            </Button>
          </span>
        </div>
      )}
    </SectionCard>
  )
}

export function PatientSummaryDrawer({ isOpen, detail, onClose }: Props) {
  const [reviews, setReviews] = useState<Partial<Record<SectionId, AiReview>>>({})
  // Only the most recent engagement plan starts open; older ones open on click.
  // Unset means "default", so the first meeting is open even if the data
  // arrives after mount.
  const [openMeetings, setOpenMeetings] = useState<Record<string, boolean>>({})
  const isMeetingOpen = (id: string, index: number) => openMeetings[id] ?? index === 0

  const review = (id: SectionId) => ({
    review: reviews[id],
    onReview: (next: AiReview | undefined) => setReviews(prev => ({ ...prev, [id]: next })),
  })

  // Escape closes, matching every other dismissible surface in the app.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-hidden={!isOpen}
      aria-label="Patient summary details"
      style={{
        position: 'fixed', top: 0, right: 0, zIndex: 30,
        height: '100vh',
        // Same clamp as CareBlockDrawer: half the viewport, usable when
        // narrow, never swallowing the profile when wide.
        width: '50%', minWidth: 496, maxWidth: '70%',
        background: 'var(--canvas)',
        borderLeft: '1px solid var(--cloud)',
        boxShadow: isOpen ? '-8px 0 24px rgba(4, 51, 56, 0.10)' : 'none',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        visibility: isOpen ? 'visible' : 'hidden',
        pointerEvents: isOpen ? 'auto' : 'none',
        transition: `transform 260ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear ${isOpen ? '0s' : '260ms'}`,
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* ── Header ── */}
      <div style={{
        flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid var(--cloud)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        {/* The Ayble AI mark, as on the block drawer's AI entries. */}
        <span aria-hidden style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: 'var(--marigold)', color: 'var(--seafoam)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AybleMarkIcon size={18} />
        </span>
        <h2 style={{
          flex: 1, minWidth: 0, margin: 0,
          fontFamily: 'var(--font-heading)',
          fontSize: 'var(--text-h4-size)', lineHeight: 'var(--text-h4-lh)',
          fontWeight: 'var(--text-h4-weight)', color: 'var(--evergreen)',
        }}>
          Patient Summary
        </h2>
        <Pressable
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="hover-evergreen"
          w="26px"
          h="26px"
          borderRadius="full"
          borderWidth="1px"
          borderColor="cloud"
          bg="canvas"
          color="seafoam"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          <CloseIcon />
        </Pressable>
      </div>

      {/* ── Sections ── */}
      <div style={{
        flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px',
        display: 'flex', flexDirection: 'column', gap: 22,
      }}>
        <Section title="Highlights since your last visits" {...review('highlights')}>
          <SectionCard>
            <ul style={{ ...bulletList, margin: 0, padding: '12px 14px 12px 32px' }}>
              {detail.highlights.map(h => <li key={h}>{h}</li>)}
            </ul>
          </SectionCard>
        </Section>

        <Section title="Patient progress" {...review('progress')}>
          <SectionCard>
            <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                <Chip variant={valenceChipVariant('positive')}>What's working</Chip>
                <ul style={{ ...bulletList, alignSelf: 'stretch' }}>
                  {detail.progress.working.map(w => <li key={w}>{w}</li>)}
                </ul>
              </div>
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start',
                borderTop: '1px solid var(--cloud)', paddingTop: 12,
              }}>
                <Chip variant={valenceChipVariant('negative')}>What's challenging</Chip>
                <ul style={{ ...bulletList, alignSelf: 'stretch' }}>
                  {detail.progress.challenging.map(c => <li key={c}>{c}</li>)}
                </ul>
              </div>
            </div>
          </SectionCard>
        </Section>

        <Section title="Recent patient sentiment" {...review('sentiment')}>
          {/* Feather, as the block drawer sets the member's own voice apart. */}
          <SectionCard background="var(--feather)">
            <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ ...bodyText, fontStyle: 'italic', paddingLeft: 10, borderLeft: '2px solid var(--marigold)' }}>
                “{detail.sentiment.quote}”
              </p>
              <p style={{ ...bodyText, fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)' }}>
                {detail.sentiment.source} · {detail.sentiment.date}
              </p>
              <p style={bodyText}>{detail.sentiment.read}</p>
            </div>
          </SectionCard>
        </Section>

        <Section title="Care journey overview" {...review('journey')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {detail.meetings.map((meeting, i) => (
              <MeetingRow
                key={meeting.id}
                meeting={meeting}
                open={isMeetingOpen(meeting.id, i)}
                onToggle={() => setOpenMeetings(prev => ({ ...prev, [meeting.id]: !isMeetingOpen(meeting.id, i) }))}
              />
            ))}
          </div>
        </Section>
      </div>
    </div>
  )
}
